import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge, Spinner } from '@atlas/ui-primitives'
import { Link } from 'react-router-dom'
import { formatUtc } from '@/lib/format-utc'
import type { WorkfrontBundleDTO } from '@/services/workfront.service'
import { bundleTitleOf } from '../shared'
import { BundleStateBadge, SyncStatusBadge } from './StatusBadges'

export interface BundlesTableProps {
	bundles: WorkfrontBundleDTO[]
	isLoading?: boolean
	isError?: boolean
	/** Where a bundle's detail lives, so the table stays free of routing decisions. */
	hrefOf: (bundle: WorkfrontBundleDTO) => string
}

const COLUMNS = ['Brief', 'Ref.', 'State', 'Children', 'Version', 'Sync', 'Last change', 'Last fetch', 'Open errors']

/**
 * A bundle known only by its program ID (discovered, or removed, before it was ever
 * fetched) has no title and no reference number yet; it reads as such, with the ID
 * Workfront knows it by. Its brief project ID is only learned by a fetch.
 */
function UnfetchedTitle({ bundle }: { bundle: WorkfrontBundleDTO }) {
	return (
		<span className="block">
			<span className="block">{bundleTitleOf(bundle)}</span>
			<span className="block font-mono font-normal text-ds-xs text-ds-text-muted">
				Workfront program {bundle.programId}
			</span>
		</span>
	)
}

function BundleRow({ bundle, href }: { bundle: WorkfrontBundleDTO; href: string }) {
	const titleContent = bundle.title?.trim() ? (
		bundleTitleOf(bundle)
	) : !bundle.lastFetchedAt ? (
		<UnfetchedTitle bundle={bundle} />
	) : (
		<span className="text-ds-text-muted">{bundleTitleOf(bundle)}</span>
	)

	return (
		<TableRow>
			<TableCell>
				<Link to={href} className="font-medium hover:underline">
					{titleContent}
				</Link>
			</TableCell>
			<TableCell>
				{bundle.referenceNumber ??
					(bundle.lastFetchedAt ? '—' : <span className="text-ds-text-muted">not known yet</span>)}
			</TableCell>
			<TableCell>
				<BundleStateBadge state={bundle.state} />
			</TableCell>
			<TableCell>{bundle.childCount}</TableCell>
			<TableCell>{bundle.version}</TableCell>
			<TableCell>
				<SyncStatusBadge status={bundle.syncStatus} />
			</TableCell>
			<TableCell>{bundle.sourceLastUpdateAt ? formatUtc(bundle.sourceLastUpdateAt) : '—'}</TableCell>
			<TableCell>{bundle.lastFetchedAt ? formatUtc(bundle.lastFetchedAt) : '—'}</TableCell>
			<TableCell>
				{bundle.openErrors > 0 ? (
					<Badge size="sm" variant="danger">
						{bundle.openErrors}
					</Badge>
				) : (
					'—'
				)}
			</TableCell>
		</TableRow>
	)
}

/** The bundles, one row each, linking to their detail. Prop-driven; the bundles page owns the fetching. */
export function BundlesTable({ bundles, isLoading, isError, hrefOf }: BundlesTableProps) {
	if (isLoading) return <Spinner aria-label="Loading bundles" />
	if (isError) return <EmptyState title="Could not load the bundles" description="Try again in a moment." />
	if (bundles.length === 0) return <EmptyState title="No bundles" description="None match these filters." />
	return (
		<Table aria-label="Bundles">
			<TableHeader>
				<TableRow>
					{COLUMNS.map((column) => (
						<TableHead key={column}>{column}</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{bundles.map((bundle) => (
					<BundleRow key={bundle.id} bundle={bundle} href={hrefOf(bundle)} />
				))}
			</TableBody>
		</Table>
	)
}
